import React from 'react';
// hero background import removed to avoid module resolution issues; use public path instead
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, CheckCircle, X, Apple, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useLanguage } from '../LanguageProvider';

// Masaüstü ve mobil için ayrı slider görsel listeleri
const DESKTOP_SLIDESHOW_IMAGES = ['/1_v2.png', '/2_v2.png', '/3_v2.png', '/4_v3.png'];
const MOBILE_SLIDESHOW_IMAGES = ['/11.png', '/22.png', '/33.png', '/44_v3.png'];

export function HomePage() {
  const heroRef = useRef<HTMLDivElement | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const { language } = useLanguage();

  // Referans verileri - marquee için yapılandırılmış liste
  const references: Array<
    | { type: 'icon'; name: string; color?: string; className?: string }
    | { type: 'image'; name: string; src: string; style?: React.CSSProperties }
  > = [
      { type: 'icon', name: 'TALYA FRESH', color: '#c21111' },
      { type: 'image', name: 'GÜLBUDAK', src: '/ref_gulbudak.avif', style: { height: '40px', width: '150px' } },
      { type: 'icon', name: 'YER GÖK ELMA', className: 'text-primary' },
      { type: 'icon', name: 'TURKUAZ', color: '#c21111' },
      { type: 'image', name: 'ÇAVSA COLD STORAGE', src: '/ref_cavsa.svg', style: { height: '60px' } },
      { type: 'image', name: 'TAŞBAŞ SOĞUK HAVA DEPOSU', src: '/ref_tasbas.jpg', style: { height: '80px' } },
      { type: 'image', name: 'ONURLU FRESH FRUIT', src: '/ref_onurlu.png', style: { height: '60px' } },
      { type: 'image', name: 'ERİKÇİ TARIM', src: '/ref_erikci.webp', style: { height: '60px' } },
      { type: 'image', name: 'KAPAR SOĞUK HAVA', src: '/ref_kapar.png', style: { height: '60px', width: '170px' } },
      { type: 'image', name: 'SWEET TARIM', src: '/ref_sweet.png', style: { height: '60px' } },
      { type: 'image', name: 'GOLD FRESH', src: '/ref_golfresh.png', style: { height: '60px' } },
      { type: 'icon', name: 'KIREFE', className: 'text-primary' },
      { type: 'icon', name: 'YILDIZ', className: 'text-primary' },
      { type: 'icon', name: 'KASAPOĞLU', color: '#c21111' },
      { type: 'icon', name: 'EKREMOĞLU', className: 'text-primary' },
      { type: 'icon', name: 'MEYSO', color: '#c21111' },
    ];

  const texts = {
    tr: {
      featuredTitle: 'Öne Çıkan Ürünlerimiz',
      featuredSubtitle: 'İnovatif çözümlerimizle sektörde fark yaratan ürünlerimizi keşfedin',
      exploreMobileApp: 'Mobil Uygulamayı Keşfedin',
      viewDetails: 'Detayları İncele',
      viewAllProducts: 'Tüm Ürünleri Görüntüle',
      aboutTitle: 'Teknoloji ile Hayatı Kolaylaştırıyoruz',
      aboutText:
        '2011 yılından beri elektronik ve yazılım alanında faaliyet gösteren İlimera Teknoloji, müşteri ihtiyaçlarına özel çözümler geliştiren öncü bir şirkettir.',
      mission: 'Misyonumuz',
      missionText: 'Teknoloji ile hayatı kolaylaştırmak ve güvenilir çözümler üretmek',
      vision: 'Vizyonumuz',
      visionText: 'Yerli ve yenilikçi teknolojilerle global pazarda söz sahibi olmak',
      values: 'Değerlerimiz',
      valuesText: 'Kalite, güvenilirlik ve müşteri memnuniyeti odaklı yaklaşım',
      supports: 'Destekler',
      references: 'Referanslar',
    },
    en: {
      featuredTitle: 'Featured Products',
      featuredSubtitle: 'Discover our innovative products that make a difference in the industry',
      exploreMobileApp: 'Explore Mobile App',
      viewDetails: 'View Details',
      viewAllProducts: 'View All Products',
      aboutTitle: 'We Simplify Life with Technology',
      aboutText:
        'Since 2011, İlimera Technology has been a pioneering company in electronics and software, developing tailor-made solutions for customer needs.',
      mission: 'Our Mission',
      missionText: 'To make life easier with technology and deliver reliable solutions',
      vision: 'Our Vision',
      visionText: 'To become a global player with local and innovative technologies',
      values: 'Our Values',
      valuesText: 'A focus on quality, reliability and customer satisfaction',
      supports: 'Supports',
      references: 'References',
    },
  } as const;

  const t = texts[language] ?? texts.tr;
  const getLocalized = <T extends Record<'tr' | 'en', any>>(value: T) => value[language] ?? value.tr;
  // Home hero üstünde dönecek görseller
  const [slideshowImages, setSlideshowImages] = useState<string[]>(DESKTOP_SLIDESHOW_IMAGES);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const slideshowIntervalRef = useRef<number | null>(null);
  const SLIDESHOW_DELAY = 5500; // 5.5 saniye

  // Zamanlayıcıyı başlat/sıfırla
  const resetSlideshowTimer = useCallback(() => {
    // Mevcut zamanlayıcıyı temizle
    if (slideshowIntervalRef.current) {
      window.clearInterval(slideshowIntervalRef.current);
    }
    // Yeni zamanlayıcı başlat
    slideshowIntervalRef.current = window.setInterval(() => {
      setCurrentSlideIndex((prevIndex) => (prevIndex + 1) % slideshowImages.length);
    }, SLIDESHOW_DELAY);
  }, [slideshowImages.length]);

  const handleNextSlide = useCallback(() => {
    setCurrentSlideIndex((prevIndex) => (prevIndex + 1) % slideshowImages.length);
    resetSlideshowTimer(); // Zamanlayıcıyı sıfırla
  }, [slideshowImages.length, resetSlideshowTimer]);

  const handlePrevSlide = useCallback(() => {
    setCurrentSlideIndex((prevIndex) => (prevIndex - 1 + slideshowImages.length) % slideshowImages.length);
    resetSlideshowTimer(); // Zamanlayıcıyı sıfırla
  }, [slideshowImages.length, resetSlideshowTimer]);

  // Partiküller başlatıldı mı flag'i - tekrar başlatmayı önler
  const particlesInitialized = useRef(false);

  // Partikülleri başlat
  const initParticles = useCallback(() => {
    // Zaten başlatıldıysa tekrar başlatma
    if (particlesInitialized.current) return;

    const $ = (window as any).jQuery as any;
    const pg = (window as any).particleground as any;
    if (!heroRef.current || !$.fn || !pg) return;
    const containerEl = heroRef.current.parentElement as HTMLElement | null;
    if (!containerEl) return;
    const $el = $(heroRef.current);

    // Zaten başlatıldıysa tekrar başlatma
    if ($el.data('pg-init')) return;
    $el.data('pg-init', true);

    const rect = containerEl.getBoundingClientRect();
    heroRef.current.style.width = `${rect.width}px`;
    heroRef.current.style.height = `${rect.height}px`;
    heroRef.current.style.top = '0px';
    heroRef.current.style.left = '0px';

    $el.particleground({
      dotColor: 'rgba(93, 101, 107, 0.35)',
      lineColor: 'rgba(93, 101, 107, 0.35)',
      parallax: true,
      parallaxMultiplier: 50,
      particleCount: 70,
      proximity: 100,
      curvedLines: true
    });

    particlesInitialized.current = true;
  }, []);

  // Partikülleri yeniden boyutlandır (destroy etmeden)
  const resizeParticles = useCallback(() => {
    if (!heroRef.current) return;
    const containerEl = heroRef.current.parentElement as HTMLElement | null;
    if (!containerEl) return;
    const rect = containerEl.getBoundingClientRect();
    heroRef.current.style.width = `${rect.width}px`;
    heroRef.current.style.height = `${rect.height}px`;
  }, []);

  useEffect(() => {
    // Ekran genişliğine göre mobil/masaüstü görsel setini seç
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');

    const updateImages = (e?: MediaQueryListEvent) => {
      const isMobile = e ? e.matches : mq.matches;
      setSlideshowImages(isMobile ? MOBILE_SLIDESHOW_IMAGES : DESKTOP_SLIDESHOW_IMAGES);
      setCurrentSlideIndex(0);
      resetSlideshowTimer();
    };

    updateImages();
    mq.addEventListener('change', updateImages);

    return () => {
      mq.removeEventListener('change', updateImages);
    };
  }, [resetSlideshowTimer]);

  useEffect(() => {
    // Tema kontrolü
    const checkTheme = () => {
      const isDark = document.documentElement.classList.contains('dark');
      console.log('Tema kontrolü:', { isDark, classList: document.documentElement.classList.toString() });
      setIsDarkMode(isDark);
    };

    // İlk kontrol
    checkTheme();

    // Tema değişikliklerini dinle
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Hero sağındaki görseli otomatik değiştir
  useEffect(() => {
    if (slideshowImages.length === 0) return;
    resetSlideshowTimer();
    return () => {
      if (slideshowIntervalRef.current) {
        window.clearInterval(slideshowIntervalRef.current);
      }
    };
  }, [resetSlideshowTimer, slideshowImages.length]);

  // Partikülleri sayfa tam yüklendikten sonra başlat
  useEffect(() => {
    let timeoutId: number | null = null;

    const startParticles = () => {
      // Küçük bir gecikme ile başlat - DOM'un tam render olmasını bekle
      timeoutId = window.setTimeout(() => {
        initParticles();
        // İlk başlatmadan sonra boyutları güncelle
        resizeParticles();
      }, 100);
    };

    // Sayfa zaten yüklendiyse hemen başlat, yoksa bekle
    if (document.readyState === 'complete') {
      startParticles();
    } else {
      window.addEventListener('load', startParticles);
    }

    // Pencere boyutu değiştiğinde partikülleri yeniden boyutlandır
    const handleResize = () => {
      resizeParticles();
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      window.removeEventListener('load', startParticles);
      window.removeEventListener('resize', handleResize);

      const $ = (window as any).jQuery as any;
      if (heroRef.current && $.fn) {
        const $el = $(heroRef.current);
        try { $el.particleground('destroy'); } catch { }
        $el.data('pg-init', false);
      }
      particlesInitialized.current = false;
    };
  }, [initParticles, resizeParticles]);
  const featuredProjects = [
    {
      slug: 'frigolive',
      title: { tr: 'Frigolive F-007', en: 'Frigolive F-007' },
      description: {
        tr: 'Tedarik zincirinizde tam kontrol: Konum, sıcaklık, nem, ışık ve darbe verileriyle hassas ürün transferlerinizi garanti altına alın.',
        en: 'Full control over your cold chain: Secure shipments with live location, temperature, humidity, light, and shock data.',
      },
      image: '/frigolive-1.png',
      features: {
        tr: ['Canlı takip', 'GSM bağlantısı', 'Alarm sistemi', 'Mobil uygulama'],
        en: ['Live tracking', 'GSM connectivity', 'Alarm system', 'Mobile app'],
      },
    },
    {
      slug: 'shalteron',
      title: { tr: 'SHALTERON', en: 'SHALTERON' },
      description: {
        tr: 'SHALTERON, sahadaki elektrikli sistemlerinizi mobil uygulama üzerinden pratik, güvenilir ve uzaktan yönetmenizi sağlayan güçlü bir otomasyon çözümüdür.',
        en: 'SHALTERON is a powerful automation solution that lets you manage your field electrical systems practically, reliably and remotely via the mobile app.',
      },
      image: '/shalteron_cihaz_v3.png',
      features: {
        tr: ['Bahçe Sulama', 'Motor Kontrolü', 'Zamanlama', 'Mobil uygulama'],
        en: ['Garden irrigation', 'Motor control', 'Scheduling', 'Mobile app'],
      },
    },
    {
      slug: 'shalteron-s',
      title: { tr: 'SHALTERON S', en: 'SHALTERON S' },
      description: {
        tr: 'Uzaktan kontrol ve entegre sıcaklık/nem sensörü ile saha otomasyonunda daha fazla görünürlük; 6 röle, 4 dijital giriş, anlık bildirim.',
        en: 'Remote control plus onboard temperature and humidity sensing—six relays, four digital inputs and instant notifications.',
      },
      image: '/shalteron_cihaz_v3.png',
      features: {
        tr: ['Sıcaklık/nem izleme', 'Röle kontrolü', 'Bildirimler', 'Mobil uygulama'],
        en: ['Temp/humidity monitoring', 'Relay control', 'Alerts', 'Mobile app'],
      },
    },
    {
      slug: 'akilli-gecis-sistemi',
      title: { tr: 'Akıllı Geçiş Sistemi', en: 'Smart Access System' },
      description: {
        tr: 'İlimera AGS; kurumsal tesisler, üniversite kampüsleri ve yoğun geçişli alanlar için kartlı geçiş, mobil erişim ve dinamik QR doğrulamayı tek platformda toplayan ölçeklenebilir bir erişim kontrol çözümüdür.',
        en: 'İlimera AGS is a scalable access platform for corporate sites, campuses and high-traffic areas-unifying card access, mobile credentials and dynamic QR verification.',
      },
      image: '/kgs_1.png',
      features: {
        tr: ['QR kod desteği', 'Yoklama sistemi', 'Raporlama', 'Mobil uygulama'],
        en: ['QR code support', 'Attendance system', 'Reporting', 'Mobile app'],
      },
    },
  ] satisfies Array<{
    slug: string;
    image: string;
    title: Record<'tr' | 'en', string>;
    description: Record<'tr' | 'en', string>;
    features: Record<'tr' | 'en', string[]>;
  }>;

  return (
    <div>
      {/* Fuar Duyuru Banner */}
      {showAnnouncement && (
        <div className="relative w-full bg-gradient-to-r from-green-600 to-green-700 text-white">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: "url('/cta3-bg.jpg')" }}
          />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <button
              onClick={() => setShowAnnouncement(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
              aria-label="Duyuruyu kapat"
            >
              <X size={20} />
            </button>

            <div className="pr-10">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-3">
                İlimera Teknoloji Euroasia Interfresh 2025'te!
              </h2>

              <div className="space-y-3 text-sm sm:text-base leading-relaxed">
                <p>
                  <span className="font-semibold">18-20 Eylül 2025</span> tarihlerinde İzmir'de düzenlenecek olan Meyve, Sebze, Gıda, Gıda Ürünleri, Gıda Teknolojileri, Ambalaj, Tarım Teknolojileri, Depolama ve Lojistik Fuarı –
                  <span className="font-bold"> Euroasia Interfresh 2025'te biz de yerimizi alıyoruz.</span>
                </p>

                <p>
                  Soğuk zincir yönetimi, gıda güvenliği, akıllı lojistik ve tarım teknolojileri alanında en son yeniliklerin sergileneceği bu büyük buluşmada,
                  <span className="font-extrabold"> İLİMERA TEKNOLOJİ</span> olarak geliştirdiğimiz çözümleri sizlerle paylaşacağız.
                </p>

                <div className="bg-white/10 rounded-lg p-4 mt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-semibold">Fuar Adresi:</span><br />
                      Zafer Sakarya Caddesi No:2 Yeni Fuar Alanı, Gaziemir/İzmir
                    </div>
                    <div>
                      <span className="font-semibold">Stand No:</span> A 10A
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative overflow-hidden h-[845px] py-0 flex items-center justify-center">
        {/* Background image */}
        <div
          aria-hidden
          className="absolute inset-0 z-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/hero3-bg.jpg')" }}
        ></div>
        {/* Dark overlay for readability */}
        <div aria-hidden className="absolute inset-0 z-[5] bg-black/45"></div>
        {/* Particles above overlay */}
        <div ref={heroRef} id="particles" className="absolute inset-0 z-10 pointer-events-none"></div>
        {/* Subtle grid texture */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5 z-[15]"></div>
        <div className="relative z-20 w-full flex flex-col items-center">
          <div
            className="relative w-full max-w-[1234px]"
            style={{ aspectRatio: '1234 / 845', maxHeight: '845px' }}
          >
            <ImageWithFallback
              src={slideshowImages[currentSlideIndex]}
              alt="Teknoloji ve İnovasyon"
              className="w-full h-full object-contain rounded-lg shadow-xl"
            />
            {/* Masaüstü: Butonlar görselin içinde, alt ortada */}
            <div className="hidden md:flex absolute inset-x-0 bottom-8 justify-center gap-6">
              <button
                type="button"
                onClick={handlePrevSlide}
                className="flex items-center justify-center w-12 h-12 rounded-full bg-black/60 text-white hover:bg-black/75 active:bg-black/80 transition-all duration-150 shadow-2xl border border-white/20"
                aria-label="Önceki görsel"
              >
                <ChevronLeft className="w-7 h-7" strokeWidth={2} />
              </button>
              <button
                type="button"
                onClick={handleNextSlide}
                className="flex items-center justify-center w-12 h-12 rounded-full bg-black/60 text-white hover:bg-black/75 active:bg-black/80 transition-all duration-150 shadow-2xl border border-white/20"
                aria-label="Sonraki görsel"
              >
                <ChevronRight className="w-7 h-7" strokeWidth={2} />
              </button>
            </div>
          </div>
          {/* Mobil: Butonlar görselin altında */}
          <div className="flex md:hidden justify-center gap-6 mt-4">
            <button
              type="button"
              onClick={handlePrevSlide}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-black/60 text-white hover:bg-black/75 active:bg-black/80 transition-all duration-150 shadow-2xl border border-white/20"
              aria-label="Önceki görsel"
            >
              <ChevronLeft className="w-6 h-6" strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={handleNextSlide}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-black/60 text-white hover:bg-black/75 active:bg-black/80 transition-all duration-150 shadow-2xl border border-white/20"
              aria-label="Sonraki görsel"
            >
              <ChevronRight className="w-6 h-6" strokeWidth={2} />
            </button>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              {t.featuredTitle}
            </h2>
            <p className="text-lg text-foreground">
              {t.featuredSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
            {featuredProjects.map((project) => (
              <Card key={project.slug} className="overflow-hidden hover:shadow-lg transition-shadow flex flex-col h-full">
                <div className="w-full aspect-[16/9]">
                  <ImageWithFallback
                    src={project.image}
                    alt={getLocalized(project.title)}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6 flex flex-col flex-1">
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    {getLocalized(project.title)}
                  </h3>
                  <p className="text-muted mb-4">
                    {getLocalized(project.description)}
                  </p>
                  <ul className="mb-6 flex-1">
                    {getLocalized(project.features).map((feature, index) => (
                      <li key={index} className="flex items-center text-sm text-muted-foreground mb-1">
                        <span className="flex-shrink-0 inline-flex items-center justify-center w-4 h-4 mr-2" aria-hidden>
                          <svg className="shalteron-tik-svg" width="16" height="16" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                            <path d="M11.5 3.5L5.5 9.5L2.5 6.5" stroke="#348d5c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto space-y-2">
                    {project.slug && (
                      <Link to={`/downloads/${project.slug}`}>
                        <Button variant="outline" className="w-full group dark:bg-gray-800 dark:border-gray-800">
                          {t.exploreMobileApp}
                          <ArrowRight className="ml-2" size={16} />
                        </Button>
                      </Link>
                    )}
                    <div className="h-1.4" /> {/* Butonlar arası boşluk */}
                    <Link to={`/urunler/${project.slug}`}>
                      <Button variant="outline" className="w-full group dark:bg-gray-800 dark:border-gray-800">
                        {t.viewDetails}
                        <ArrowRight className="ml-2" size={16} />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/urunler">
              <Button size="lg" className="bg-primary hover:bg-primary/90">
                {t.viewAllProducts}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Summary */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              {t.aboutTitle}
            </h2>
            <p className="text-lg text-foreground max-w-3xl mx-auto">
              {t.aboutText}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                <Target className="text-primary" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2 transition-all duration-300 group-hover:text-primary group-hover:drop-shadow-sm relative after:block after:h-0.5 after:w-0 after:bg-primary after:mx-auto after:mt-2 after:transition-all after:duration-300 group-hover:after:w-12">
                {t.mission}
              </h3>
              <p className="text-muted transition-colors duration-300 group-hover:text-foreground">
                {t.missionText}
              </p>
            </div>

            <div className="text-center group">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3">
                <Eye className="text-primary" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2 transition-all duration-300 group-hover:text-primary group-hover:drop-shadow-sm relative after:block after:h-0.5 after:w-0 after:bg-primary after:mx-auto after:mt-2 after:transition-all after:duration-300 group-hover:after:w-12">
                {t.vision}
              </h3>
              <p className="text-muted transition-colors duration-300 group-hover:text-foreground">
                {t.visionText}
              </p>
            </div>

            <div className="text-center group">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-1">
                <CheckCircle className="text-primary" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2 transition-all duration-300 group-hover:text-primary group-hover:drop-shadow-sm relative after:block after:h-0.5 after:w-0 after:bg-primary after:mx-auto after:mt-2 after:transition-all after:duration-300 group-hover:after:w-12">
                {t.values}
              </h3>
              <p className="text-muted transition-colors duration-300 group-hover:text-foreground">
                {t.valuesText}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Destekler Section */}
      <section className="py-12 lg:py-16 relative z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl lg:text-4xl font-bold text-foreground mb-4"
            style={{ color: 'var(--foreground)' }}
          >
            {t.supports}
          </h2>
          <div className="flex justify-center items-center gap-12 mb-12">
            <img
              src={isDarkMode ? "/tubitak-Dikey-Beyaz.png" : "/tubitak-Dikey.png"}
              alt="TÜBİTAK"
              className="w-auto object-contain"
              style={{ height: '98px' }}
            />
            <img
              src={isDarkMode ? "/kosgeb-logo-beyaz.png" : "/kosgeb-logo-renkli.png"}
              alt="KOSGEB"
              className="w-auto object-contain"
              style={{ height: '70px' }}
            />
          </div>
        </div>
      </section>

      {/* Referanslar Section - Otomatik kayan marquee */}
      <section className="py-12 lg:py-16 relative z-10 overflow-hidden">
        <style>{`
          @keyframes marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-marquee {
            animation: marquee 40s linear infinite;
            width: max-content;
          }
        `}</style>

        <div className="w-full text-center mb-10 px-4">
          <h2
            className="text-3xl lg:text-4xl font-bold text-foreground"
            style={{ color: 'var(--foreground)' }}
          >
            {t.references}
          </h2>
        </div>

        <div className="relative w-full overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />

          <div className="flex animate-marquee items-center gap-12">
            {[...references, ...references].map((ref, index) => (
              <div key={index} className="flex flex-col items-center justify-center min-w-[160px] select-none shrink-0 px-2">
                {ref.type === 'icon' ? (
                  <Apple
                    className={ref.className ? `${ref.className} mb-3` : 'mb-3'}
                    size={48}
                    color={ref.color}
                  />
                ) : (
                  <img
                    src={ref.src}
                    alt={ref.name}
                    className="w-auto object-contain mb-3 grayscale hover:grayscale-0 transition-all duration-300 opacity-80 hover:opacity-100"
                    style={ref.style || { height: '60px' }}
                  />
                )}
                <span className="text-sm text-muted-foreground text-center font-medium whitespace-nowrap">
                  {ref.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}